import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import SubscriptionEnrollmentService from "services/SubscriptionEnrollmentService";

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || "Internal server error";

export const fetchSubscriptionEnrollments = createAsyncThunk(
  "subscriptionEnrollments/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await SubscriptionEnrollmentService.getEnrollments(
        params
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchSubscriptionEnrollment = createAsyncThunk(
  "subscriptionEnrollments/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const response = await SubscriptionEnrollmentService.getEnrollment(id);
      return response.data?.enrollment;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const validateSubscriptionEnrollment = createAsyncThunk(
  "subscriptionEnrollments/validate",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await SubscriptionEnrollmentService.validateEnrollment(
        id,
        data
      );
      return response.data?.enrollment;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const rejectSubscriptionEnrollment = createAsyncThunk(
  "subscriptionEnrollments/reject",
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await SubscriptionEnrollmentService.rejectEnrollment(
        id,
        reason
      );
      return response.data?.enrollment;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateSubscriptionEnrollmentReceipt = createAsyncThunk(
  "subscriptionEnrollments/updateReceipt",
  async ({ id, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("receiptFile", file);
      const response = await SubscriptionEnrollmentService.updateReceipt(
        id,
        formData
      );
      return response.data?.enrollment;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialState = {
  list: [],
  page: 1,
  pages: 1,
  totalItems: 0,
  limit: 10,
  loading: false,
  actionLoading: false,
  error: null,
  current: null,
};

const subscriptionEnrollmentsSlice = createSlice({
  name: "subscriptionEnrollments",
  initialState,
  reducers: {
    resetEnrollmentState: (state) => {
      state.current = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptionEnrollments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionEnrollments.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.list = payload?.data || [];
        state.page = payload?.page || 1;
        state.pages = payload?.pages || 1;
        state.limit = payload?.limit || 10;
        state.totalItems = payload?.totalItems || 0;
      })
      .addCase(fetchSubscriptionEnrollments.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(fetchSubscriptionEnrollment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionEnrollment.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.current = payload;
      })
      .addCase(fetchSubscriptionEnrollment.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(validateSubscriptionEnrollment.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(
        validateSubscriptionEnrollment.fulfilled,
        (state, { payload }) => {
          state.actionLoading = false;
          if (payload) {
            const currentId = state.current ? state.current._id : null;
            state.list = state.list.map((item) =>
              item?._id === payload?._id ? payload : item
            );
            state.current = currentId === payload?._id ? payload : state.current;
          }
        }
      )
      .addCase(validateSubscriptionEnrollment.rejected, (state, { payload }) => {
        state.actionLoading = false;
        state.error = payload;
      })
      .addCase(rejectSubscriptionEnrollment.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(
        rejectSubscriptionEnrollment.fulfilled,
        (state, { payload }) => {
          state.actionLoading = false;
          if (payload) {
            const currentId = state.current ? state.current._id : null;
            state.list = state.list.map((item) =>
              item?._id === payload?._id ? payload : item
            );
            state.current = currentId === payload?._id ? payload : state.current;
          }
        }
      )
      .addCase(rejectSubscriptionEnrollment.rejected, (state, { payload }) => {
        state.actionLoading = false;
        state.error = payload;
      })
      .addCase(updateSubscriptionEnrollmentReceipt.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(
        updateSubscriptionEnrollmentReceipt.fulfilled,
        (state, { payload }) => {
          state.actionLoading = false;
          if (payload) {
            const currentId = state.current ? state.current._id : null;
            state.list = state.list.map((item) =>
              item?._id === payload?._id ? payload : item
            );
            state.current = currentId === payload?._id ? payload : state.current;
          }
        }
      )
      .addCase(
        updateSubscriptionEnrollmentReceipt.rejected,
        (state, { payload }) => {
          state.actionLoading = false;
          state.error = payload;
        }
      );
  },
});

export const { resetEnrollmentState } = subscriptionEnrollmentsSlice.actions;

export default subscriptionEnrollmentsSlice.reducer;
